import React, { useMemo } from 'react';
import { Modal, PieChart, DataTable, AccessibleDataTableColumn, formatDate } from '@aidkitorg/component-library';
import { SupportedDashboardComponent, DashboardComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { useLocalizedStrings } from '../../../../Localization';
import { AdminPanelBarChart } from './AdminPanelBarChart';
import { AdminPanelLineChart } from './AdminPanelLineChart';
import { ChartDataExportDropdown } from './ChartDataExportDropdown';
import { processBarChartData } from './barChartDataUtils';

type ChartDataModalProps = {
  isOpen: boolean;
  onClose: () => void;
  component: SupportedDashboardComponent;
  queryResult?: DashboardComponentResult;
  chartHeight?: number;
};

export const ChartDataModal: React.FC<ChartDataModalProps> = ({
  isOpen,
  onClose,
  component,
  queryResult,
  chartHeight = 400,
}) => {
  const L = useLocalizedStrings();

  const chartTitle = useMemo(() => {
    if ('title' in component && component.title) {
      return typeof component.title === 'string' ? component.title : component.title['en'];
    }
    return 'Chart Data';
  }, [component]);

  const chartElement = useMemo(() => {
    if (!queryResult) return null;

    if (component.kind === 'Bar Chart') {
      return (
        <AdminPanelBarChart
          component={component}
          queryResult={queryResult}
          height={chartHeight}
          tableCaption={chartTitle}
        />
      )
    }

    if (component.kind === 'Line Chart') {
      return (
        <AdminPanelLineChart
          component={component}
          queryResult={queryResult}
          height={chartHeight}
          tableCaption={chartTitle}
          showDataTable={false}
        />
      );
    }

    if (component.kind === 'Pie Chart') {
      const nameKey = queryResult.groupKeys?.[0] || '';
      const dataKey = queryResult.summaryKey;

      let processedData = Array.isArray(queryResult.data) ? queryResult.data.map((datum: Record<string, any>) => ({
        ...datum,
        [dataKey]: datum[dataKey] != null ? Number(datum[dataKey]) : null
      })) : [];

      // Handle data summarization for pie charts (same logic as in DashboardComponentCard)
      if (processedData.length > 10) {
        processedData.sort((a, b) => (b[dataKey] || 0) - (a[dataKey] || 0));
        const topTen = processedData.slice(0, 10);
        const remaining = processedData.slice(10);
        const otherValue = remaining.reduce((sum, item) => sum + (item[dataKey] || 0), 0);

        const otherSegment = {
          [nameKey]: L.admin_panel.dashboards.other,
          [dataKey]: otherValue
        };

        processedData = [...topTen, otherSegment];
      }

      return (
        <PieChart
          showDataTable={false}
          height={chartHeight}
          data={processedData}
          dataKey={dataKey}
          nameKey={nameKey}
          tableCaption={chartTitle}
        />
      );
    }

    return null;
  }, [component, queryResult, chartHeight, chartTitle, L]);

  // Process bar chart data once using the shared utility
  const barChartResult = useMemo(() => {
    if (!queryResult || component.kind !== 'Bar Chart') return null;
    return processBarChartData({
      component: component as SupportedDashboardComponent & { kind: 'Bar Chart' },
      queryResult,
    });
  }, [component, queryResult]);

  const tableColumns = useMemo((): AccessibleDataTableColumn[] => {
    if (!queryResult) return [];

    if (component.kind === 'Bar Chart' && barChartResult) {
      const { primaryGroupField, valueKeys, hasSecondaryGrouping } = barChartResult;

      const columns: AccessibleDataTableColumn[] = [
        {
          key: primaryGroupField,
          label: component.xAxisLabel || primaryGroupField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }
      ];

      if (hasSecondaryGrouping) {
        valueKeys.forEach(secondaryValue => {
          columns.push({
            key: secondaryValue,
            label: secondaryValue,
            format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
          });
        });
      } else {
        columns.push({
          key: barChartResult.summaryKey,
          label: component.yAxisLabel || barChartResult.summaryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
        });
      }

      return columns;
    }

    if (component.kind === 'Line Chart') {
      const lineGroupKey = queryResult.groupKeys?.[0] || '';
      let bucketFormat: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year' | undefined;
      if (component.groups[0].kind === 'Bucket Date') {
        bucketFormat = component.groups[0].bucket;
      }
      const lineSummaryKey = queryResult.summaryKey || '';

      return [
        {
          key: lineGroupKey,
          label: component.xAxisLabel || lineGroupKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: any) => value ? formatDate(value, bucketFormat) : ''
        },
        {
          key: lineSummaryKey,
          label: component.yAxisLabel || lineSummaryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
        }
      ];
    }

    if (component.kind === 'Pie Chart') {
      const nameKey = queryResult.groupKeys?.[0] || '';
      const dataKey = queryResult.summaryKey;

      return [
        {
          key: nameKey,
          label: nameKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        },
        {
          key: dataKey,
          label: dataKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
        }
      ];
    }

    return [];
  }, [component, queryResult, barChartResult]);

  const tableData = useMemo(() => {
    if (!queryResult) return [];

    if (component.kind === 'Bar Chart' && barChartResult) {
      // Use processedData from shared utility (already filtered, sorted, formatted, pivoted)
      // Zero-fill missing secondary values for table display
      if (barChartResult.hasSecondaryGrouping) {
        return barChartResult.processedData.map(row => {
          const filledRow = { ...row };
          barChartResult.valueKeys.forEach(key => {
            if (!(key in filledRow)) {
              filledRow[key] = 0;
            }
          });
          return filledRow;
        });
      }
      return barChartResult.processedData;
    }

    // For other cases, use the raw data
    if (!Array.isArray(queryResult.data)) return [];
    return queryResult.data;
  }, [component, queryResult, barChartResult]);

  if (!queryResult || (component.kind !== 'Bar Chart' && component.kind !== 'Pie Chart' && component.kind !== 'Line Chart')) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => onClose()}
      title={chartTitle}
      size="fullscreen"
      className="max-w-7xl"
    >
      <div className="space-y-6">
        {/* Chart Section */}
        {chartElement}

        {/* Data Table Section */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex justify-end mb-2">
            <ChartDataExportDropdown
              data={tableData}
              columns={tableColumns}
              title={chartTitle}
            />
          </div>
          <div className="bg-white rounded border max-h-96 overflow-auto">
            <DataTable
              data={tableData}
              columns={tableColumns.map(col => ({
                header: col.label,
                accessorKey: col.key,
                cell: col.format ? ({ getValue, row }: any) => col.format!(getValue(), row.original) : ({ getValue }: any) => getValue()
              }))}
              pageSize={10}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};